import { getErrorMessage } from '@/api/errors'
import { DynamicField } from '@/components/projects/workflows/shared/DynamicField'
import { buildDefaults } from '@/components/projects/workflows/shared/dynamicFieldUtils'
import type { FormValues } from '@/components/projects/workflows/shared/dynamicFieldUtils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAdvanceChainRun, useContextSources, useWorkflowTriggerSchema } from '@/hooks'
import { extractValidationErrors } from '@/lib/validationErrors'
import type { ChainRunLink, ContextSourceListItem } from '@/types'
import { ChevronDown, ChevronRight, Loader2, Play, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { describeInputSource } from './chainLinkInputs'

interface Props {
  projectId: string
  chainRunId: string
  link: ChainRunLink | null
  mode: 'inputs' | 'manual'
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ResumeChainDialog({ projectId, chainRunId, link, mode, open, onOpenChange }: Props) {
  const isManual = mode === 'manual'
  const advanceChainRun = useAdvanceChainRun(projectId, chainRunId)
  const [formError, setFormError] = useState<string | null>(null)
  const [otherInputsOpen, setOtherInputsOpen] = useState(false)

  const { data: schema, isLoading: schemaLoading, isError: schemaFailed } = useWorkflowTriggerSchema(
    projectId,
    open && !isManual ? link?.workflowName ?? null : null,
  )

  const missingFields = useMemo(() => {
    const missing = new Set((link?.missingInputs ?? []).map(name => name.toLowerCase()))
    return (schema?.fields ?? []).filter(f => missing.has(f.name.toLowerCase()))
  }, [schema?.fields, link?.missingInputs])

  const undeclaredInputs = useMemo(() => {
    if (isManual) return []
    const declared = new Set((schema?.fields ?? []).map(f => f.name.toLowerCase()))
    return (link?.missingInputs ?? []).filter(name => !declared.has(name.toLowerCase()))
  }, [isManual, schema?.fields, link?.missingInputs])

  const otherInputNames = useMemo(
    () => (schema?.fields ?? [])
      .filter(f => !f.hidden && !missingFields.some(m => m.name === f.name))
      .map(f => f.name),
    [schema?.fields, missingFields],
  )

  const hasContextMultiSelect = missingFields.some(f => f.type === 'context-picker' && f.multiSelect)
  const { data: contextData, isLoading: contextLoading } = useContextSources(
    projectId,
    { maxResultCount: 1000 },
    undefined,
    { enabled: open && hasContextMultiSelect },
  )
  const contextItems: ContextSourceListItem[] = useMemo(() => contextData?.items ?? [], [contextData])

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<FormValues>()

  const defaultsSessionRef = useRef<string | null>(null)

  useEffect(() => {
    if (!open) {
      defaultsSessionRef.current = null
      return
    }
    if (!isManual && schemaLoading) return
    if (hasContextMultiSelect && contextLoading) return

    const sessionKey = link?.id ?? ''
    if (defaultsSessionRef.current === sessionKey) return

    defaultsSessionRef.current = sessionKey
    reset(buildDefaults(missingFields, contextItems))
  }, [open, isManual, schemaLoading, link?.id, missingFields, contextItems, contextLoading, hasContextMultiSelect, reset])

  if (!link) return null

  const inputsUnavailable = !isManual && (schemaFailed || undeclaredInputs.length > 0)
  const inputsNotPublished = !isManual && !inputsUnavailable && link.missingInputs.length === 0

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setFormError(null)
      setOtherInputsOpen(false)
    }
    onOpenChange(next)
  }

  const onSubmit = async (values: FormValues) => {
    setFormError(null)

    try {
      await advanceChainRun.mutateAsync(isManual ? undefined : JSON.stringify(values))
      toast.success('Chain resumed')
      handleOpenChange(false)
    } catch (error) {
      const fieldErrors = extractValidationErrors(error)
      if (!fieldErrors || fieldErrors.length === 0) {
        setFormError(getErrorMessage(error) ?? 'Failed to resume the chain.')
        return
      }

      const unmatched: string[] = []
      for (const fieldError of fieldErrors) {
        const matched = missingFields.find(f => f.name.toLowerCase() === fieldError.field?.toLowerCase())
        if (matched) {
          setError(matched.name, { message: fieldError.errorMessage })
        } else {
          unmatched.push(fieldError.errorMessage)
        }
      }
      if (unmatched.length > 0) setFormError(unmatched.join(' '))
    }
  }

  const OtherInputsChevron = otherInputsOpen ? ChevronDown : ChevronRight

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">Resume Chain</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-foreground">
          {isManual ? (
            <>Start link "<span className="font-bold">{link.linkName}</span>" ({link.workflowDisplayName}) now?</>
          ) : link.missingInputs.length === 0 ? (
            <>Link "<span className="font-bold">{link.linkName}</span>" ({link.workflowDisplayName}) is waiting to start.</>
          ) : (
            <>
              Link "<span className="font-bold">{link.linkName}</span>" ({link.workflowDisplayName}) is waiting for{' '}
              {link.missingInputs.length} input(s). Supplying them starts the link immediately.
            </>
          )}
        </DialogDescription>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {!isManual && schemaLoading && (
            <div className="flex items-center gap-2" role="status" aria-live="polite">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading inputs…</span>
            </div>
          )}

          {!isManual && !schemaLoading && inputsUnavailable && (
            <p role="alert" className="text-sm text-destructive" data-testid="resume-chain-schema-error">
              {schemaFailed
                ? 'The inputs of this workflow could not be loaded. Close this dialog and try again.'
                : missingFields.length === 0
                  ? 'The inputs this link waits for are not declared by its workflow, so they cannot be supplied here.'
                  : `This link waits for inputs its workflow does not declare, so none of them can be supplied here: ${undeclaredInputs.join(', ')}.`}
            </p>
          )}

          {!schemaLoading && inputsNotPublished && (
            <p role="status" className="text-sm text-muted-foreground">
              This link has not published which inputs it needs. Resuming asks the server to work them out: the link
              starts if nothing is missing, otherwise the chain pauses again with the inputs to fill in.
            </p>
          )}

          {!inputsUnavailable && missingFields.length > 0 && (
            <div className="space-y-4" data-testid="resume-chain-inputs">
              {missingFields.map(field => (
                <DynamicField
                  key={field.name}
                  field={field}
                  projectId={projectId}
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                />
              ))}
            </div>
          )}

          {otherInputNames.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setOtherInputsOpen(o => !o)}
                aria-expanded={otherInputsOpen}
                className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                <OtherInputsChevron className="h-3 w-3" />
                Other inputs of this link ({otherInputNames.length}) — already resolved
              </button>
              {otherInputsOpen && (
                <ul className="mt-2 space-y-1" data-testid="resume-chain-other-inputs">
                  {otherInputNames.map(name => {
                    const source = describeInputSource(link, name)
                    return (
                      <li key={name} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{name}</span>
                        {source.kind === 'mapping' ? (
                          <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px] break-all">{source.expression}</code>
                        ) : source.kind === 'supplied' ? (
                          <span>supplied at start</span>
                        ) : (
                          <span>workflow default</span>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          )}

          {formError && (
            <p role="alert" className="text-sm text-destructive" data-testid="resume-chain-error">{formError}</p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={advanceChainRun.isPending || (!isManual && (schemaLoading || inputsUnavailable))}
              className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold [&_svg]:size-6 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="resume-chain-confirm"
            >
              <Play className="h-6 w-6" />
              {advanceChainRun.isPending ? 'Starting…' : isManual ? 'Start link' : 'Resume'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
